import { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  RefreshCw,
  Save,
  Video,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { webinarApi } from '@/lib/webinar-api';
import { buildWebinarWaitingRoomSrcDoc } from '@/lib/webinar-waiting-room-srcdoc';
import { Switch } from '@/components/ui/Switch';
import { toast } from '@/components/ui/Toast';

const INST_PRIMARY =
  'bg-[color:var(--inst-player-primary,#0C1C38)] text-white hover:opacity-95 disabled:opacity-50';
const INST_OUTLINE =
  'border border-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_28%,transparent)] bg-white text-[color:var(--inst-player-primary,#0C1C38)] hover:bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_6%,transparent)] dark:border-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_45%,#fff)] dark:bg-slate-900 dark:text-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_88%,#fff)]';
const CARD =
  'rounded-2xl border border-[#E2E8F4] bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900';

export default function InstitutionWebinarSettingsPanel() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [html, setHtml] = useState('');
  const [saving, setSaving] = useState(false);
  const [waitingPreviewSrcDoc, setWaitingPreviewSrcDoc] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const s = await webinarApi.institutionSettings();
      setEnabled(s.webinarsEnabled);
      setHtml(s.webinarDefaultWaitingHtml ?? '');
    } catch {
      setEnabled(false);
      setHtml('');
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const body = html.trim();
    if (!body) {
      setWaitingPreviewSrcDoc('');
      return;
    }
    const build = () => {
      const root =
        (document.querySelector('.player-institution-theme') as HTMLElement | null) ??
        document.documentElement;
      setWaitingPreviewSrcDoc(buildWebinarWaitingRoomSrcDoc(body, root));
    };
    build();
    const timer = window.setTimeout(build, 400);
    return () => window.clearTimeout(timer);
  }, [html]);

  const save = async () => {
    setSaving(true);
    try {
      await webinarApi.patchInstitutionSettings({
        webinarsEnabled: enabled,
        webinarDefaultWaitingHtml: html,
      });
      toast.success(t('institution.webinars.saveSuccess'));
    } catch {
      toast.error(t('institution.webinars.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16">
        <Loader2
          className="h-9 w-9 animate-spin text-[color:var(--inst-player-primary,#0C1C38)]"
          aria-hidden
        />
        <p className="text-sm text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.common.loading')}</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 rounded-2xl border border-red-200 bg-red-50 px-6 py-12 text-center dark:border-red-900/40 dark:bg-red-950/30">
        <AlertCircle className="h-10 w-10 text-red-600 dark:text-red-400" aria-hidden />
        <p className="max-w-md text-sm text-red-800 dark:text-red-200">{t('institution.webinars.loadError')}</p>
        <button
          type="button"
          onClick={() => void load()}
          className={cn('inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold', INST_PRIMARY)}
        >
          <RefreshCw className="h-4 w-4" aria-hidden />
          {t('institution.common.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 pb-8">
      <header
        className={cn(
          CARD,
          'overflow-hidden border-0 bg-gradient-to-br from-[color:var(--inst-player-primary,#0C1C38)] to-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_72%,#1e3a5f)] p-6 text-white sm:p-8',
        )}
      >
        <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm">
              <Video className="h-6 w-6" aria-hidden />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-white/70">
                {t('institution.tabs.webinars')}
              </p>
              <h1 className="mt-1 text-2xl font-semibold tracking-tight sm:text-3xl">
                {t('institution.webinars.pageTitle')}
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-white/85">
                {t(
                  'institution.webinars.settingsIntro',
                  'Activez le module et définissez le message d’attente par défaut pour vos webinaires.',
                )}
              </p>
            </div>
          </div>
          <span
            className={cn(
              'inline-flex shrink-0 items-center gap-2 self-start rounded-full px-3 py-1.5 text-xs font-semibold',
              enabled ? 'bg-emerald-400/20 text-emerald-100' : 'bg-white/15 text-white/80',
            )}
          >
            {enabled ? (
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
            ) : (
              <AlertCircle className="h-3.5 w-3.5" aria-hidden />
            )}
            {enabled
              ? t('institution.webinars.enabledBadge')
              : t('institution.webinars.disabledBadge')}
          </span>
        </div>
      </header>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className={cn(CARD, 'p-5 sm:p-6')}>
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
            {t('institution.webinars.sectionModule')}
          </h2>
          <p className="mt-1 text-sm text-[#5B6472] dark:text-[#B6CAEC]">
            {t('institution.webinars.sectionModuleHint')}
          </p>
          <label
            className={cn(
              'mt-4 flex cursor-pointer items-center gap-4 rounded-xl border border-[#E2E8F4] px-4 py-4 transition',
              'hover:border-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_22%,transparent)]',
              'dark:border-slate-700 dark:hover:border-slate-600',
              enabled &&
                'border-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_35%,transparent)] bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_5%,transparent)]',
            )}
          >
            <Switch size="sm" checked={enabled} onChange={setEnabled} />
            <span className="text-sm font-medium text-slate-800 dark:text-slate-100">
              {t('institution.webinars.enableForInstitution')}
            </span>
          </label>
        </section>

        <section className={cn(CARD, 'flex flex-col p-5 sm:p-6')}>
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
            {t('institution.webinars.sectionWaitingRoom')}
          </h2>
          <p className="mt-1 text-sm text-[#5B6472] dark:text-[#B6CAEC]">
            {t('institution.webinars.waitingHtmlHint')}
          </p>
          <label
            htmlFor="inst-webinar-waiting-html"
            className="mt-4 block text-xs font-semibold uppercase tracking-wide text-[#5B6472] dark:text-[#B6CAEC]"
          >
            {t('institution.webinars.waitingHtmlLabel')}
          </label>
          <textarea
            id="inst-webinar-waiting-html"
            value={html}
            onChange={(e) => setHtml(e.target.value)}
            rows={6}
            className="mt-2 w-full flex-1 rounded-xl border border-[#E2E8F4] bg-[#F8FAFD] px-3 py-2.5 font-mono text-xs text-slate-800 focus:border-[color:var(--inst-player-primary,#0C1C38)] focus:outline-none focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_18%,transparent)] dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100"
            placeholder={t('institution.webinars.waitingHtmlPlaceholder')}
          />
        </section>
      </div>

      {html.trim() ? (
        <section className={cn(CARD, 'p-5 sm:p-6')}>
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
            {t('institution.webinars.waitingPreviewTitle')}
          </h2>
          <p className="mt-0.5 text-xs text-[#5B6472] dark:text-[#B6CAEC]">
            {t('institution.webinars.waitingPreviewHint')}
          </p>
          <iframe
            title={t('institution.webinars.waitingPreviewTitle')}
            srcDoc={waitingPreviewSrcDoc || undefined}
            sandbox="allow-same-origin"
            className="mt-4 block min-h-[16rem] w-full rounded-xl border border-[#E2E8F4] bg-[color:var(--inst-player-bg,#f9fafb)] dark:border-slate-600"
          />
        </section>
      ) : null}

      <div className="flex flex-wrap items-center gap-3 border-t border-[#E2E8F4] pt-2 dark:border-slate-700">
        <button
          type="button"
          disabled={saving}
          onClick={() => void save()}
          className={cn(
            'inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold transition',
            INST_PRIMARY,
          )}
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <Save className="h-4 w-4" aria-hidden />
          )}
          {saving ? t('institution.common.saving') : t('institution.common.save')}
        </button>
        <button
          type="button"
          onClick={() => void load()}
          disabled={saving}
          className={cn(
            'inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition',
            INST_OUTLINE,
          )}
        >
          <RefreshCw className="h-4 w-4" aria-hidden />
          {t('institution.common.refresh')}
        </button>
      </div>
    </div>
  );
}
